import { useState, useRef, useEffect } from 'react';
import { Check, X, Folder } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';

interface NewFolderInlineProps {
  onSubmit: (title: string) => Promise<void>;
  onCancel: () => void;
}

export function NewFolderInline({ onSubmit, onCancel }: NewFolderInlineProps) {
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async () => {
    const trimmed = title.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="flex items-center gap-2 rounded-md border border-primary bg-primary-muted px-3 py-2">
      <Folder className="h-5 w-5 shrink-0 text-primary" />
      <input
        ref={inputRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Folder name"
        disabled={submitting}
        className="min-w-0 flex-1 border-none bg-transparent text-sm text-accent-foreground outline-none placeholder:text-muted-foreground"
      />
      {submitting ? (
        <Spinner size="sm" />
      ) : (
        <>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!title.trim()}
            className="rounded p-0.5 text-primary hover:bg-primary-light disabled:opacity-40"
            title="Create folder"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-muted-foreground"
            title="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}
